import React from 'react';
import { Button, Drawer, Empty, Skeleton, Tabs, Tooltip } from 'antd';
import {
  AudioOutlined,
  CloseOutlined,
  EditOutlined,
  LockOutlined,
  MessageOutlined,
  PhoneOutlined,
} from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { ContactDisplay, formatContactLastInteraction } from '../types/ContactDisplay';
import { CONTACT_EXCHANGES_FILTERS, ContactExchange } from '../types/ContactExchange';
import useContactExchangesController from '../controllers/useContactExchangesController';
import ConciergeBellIcon from '../../receptionist_ai/components/ConciergeBellIcon';
import { ContactHeatPill, ContactTagList } from './ContactTags';
import { CONVERSATIONS_PAGE_PATH } from '../../conversations/ConversationsPage';

const SOON_LABEL = 'Bientôt disponible';

interface ContactDrawerProps {
  contact: ContactDisplay | null;
  onClose: () => void;
  // False when the drawer is opened from the conversation itself
  canOpenConversation?: boolean;
}

export const SparkleIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9L12 3z" />
    <path d="M19 15l.8 2.2L22 18l-2.2.8L19 21l-.8-2.2L16 18l2.2-.8L19 15z" />
  </svg>
);

const formatActivityDay = (timestamp: number) => {
  const day = dayjs(timestamp);
  if (day.isSame(dayjs(), 'day')) return "Aujourd'hui";
  if (day.isSame(dayjs().subtract(1, 'day'), 'day')) return 'Hier';
  return day.format('D MMMM YYYY');
};

const Field: React.FC<React.PropsWithChildren<{ label: string }>> = ({ label, children }) => (
  <div className="contacts__field">
    <span className="contacts__info-label">{label}</span>
    <div className="contacts__field-value">{children}</div>
  </div>
);

const Section: React.FC<React.PropsWithChildren<{ title: string }>> = ({ title, children }) => (
  <section className="contacts__section">
    <div className="contacts__section-title">{title}</div>
    {children}
  </section>
);

const OverviewTab: React.FC<{ contact: ContactDisplay }> = ({ contact }) => (
  <div className="contacts__drawer-sections">
    {contact.isConfidential ? (
      <section className="contacts__summary contacts__summary--confidential">
        <LockOutlined className="contacts__confidential-icon" />
        <div className="contacts__confidential-content">
          <div className="contacts__summary-title">Mode confidentiel activé</div>
          <p className="contacts__muted">
            Aucune analyse IA n'est générée pour ce contact. Sa fiche et ses messages restent accessibles.
          </p>
        </div>
      </section>
    ) : (
      <section className="contacts__summary">
        <div className="contacts__summary-title">
          <span className="contacts__summary-icon"><SparkleIcon /></span>
          Synthèse IA
        </div>
        {contact.summaryMarkdown ? (
          <>
            <div className="contacts__summary-markdown">
              <ReactMarkdown>{contact.summaryMarkdown}</ReactMarkdown>
            </div>
            <div className="contacts__summary-date">Actualisée le {dayjs(contact.updated).format('D MMM')}</div>
          </>
        ) : (
          <p className="contacts__muted">Pas encore de synthèse pour ce contact.</p>
        )}
      </section>
    )}

    <Section title="Coordonnées">
      <div className="contacts__phones">
        <span className="contacts__info-label">Mobile</span>
        {contact.phone ? (
          <a className="contacts__link" href={`tel:${contact.phone.replace(/\s/g, '')}`}>{contact.phone}</a>
        ) : (
          <span>-</span>
        )}
      </div>
      <div className="contacts__fields">
        <Field label="Email">
          {contact.email ? <a className="contacts__link" href={`mailto:${contact.email}`}>{contact.email}</a> : '-'}
        </Field>
        <Field label="Entreprise">{contact.company || '-'}</Field>
      </div>
    </Section>

    <QualificationSection contact={contact} />
  </div>
);

const QualificationSection: React.FC<{ contact: ContactDisplay }> = ({ contact }) => (
  <Section title="Qualification">
    <div className="contacts__fields">
      <Field label="Chaleur">{contact.heat ? <ContactHeatPill heat={contact.heat} level={contact.heatLevel} /> : '-'}</Field>
      <Field label="Typologie">{contact.typologies.length > 0 ? <ContactTagList tags={contact.typologies} /> : '-'}</Field>
      {contact.status && <Field label="Statut"><ContactTagList tags={[contact.status]} /></Field>}
    </div>
    <Field label="Tags personnalisés">
      {contact.customTags.length > 0 ? <ContactTagList tags={contact.customTags} /> : '-'}
    </Field>
  </Section>
);

const AboutRow: React.FC<React.PropsWithChildren<{ label: string }>> = ({ label, children }) => (
  <div className="contacts__about-row">
    <span className="contacts__info-label">{label}</span>
    <span>{children}</span>
  </div>
);

const InformationsTab: React.FC<{ contact: ContactDisplay }> = ({ contact }) => (
  <div className="contacts__drawer-sections">
    <Section title="Coordonnées">
      <div className="contacts__phones">
        <span className="contacts__info-label">Mobile</span>
        {contact.phone ? (
          <a className="contacts__link" href={`tel:${contact.phone.replace(/\s/g, '')}`}>{contact.phone}</a>
        ) : (
          <span>-</span>
        )}
      </div>
      <div className="contacts__fields">
        <Field label="Email">
          {contact.email ? <a className="contacts__link" href={`mailto:${contact.email}`}>{contact.email}</a> : '-'}
        </Field>
        <Field label="Entreprise">{contact.company || '-'}</Field>
        <Field label="Poste">{contact.role || '-'}</Field>
        <Field label="Site web">
          {contact.website ? (
            <a
              className="contacts__link"
              href={/^https?:\/\//.test(contact.website) ? contact.website : `https://${contact.website}`}
              target="_blank"
              rel="noreferrer"
            >
              {contact.website}
            </a>
          ) : (
            '-'
          )}
        </Field>
        <div className="contacts__field--wide">
          <Field label="Adresse">{contact.address || '-'}</Field>
        </div>
      </div>
    </Section>

    <QualificationSection contact={contact} />

    <Section title="À propos de cette fiche">
      <div className="contacts__about">
        <AboutRow label="Créée le">
          {contact.created
            ? `${dayjs(contact.created).format('D MMMM YYYY')} · ${formatContactLastInteraction(contact.created).toLowerCase()}`
            : '-'}
        </AboutRow>
        {/* The light payload has no source */}
        <AboutRow label="Source">-</AboutRow>
        <AboutRow label="Suivi par">{contact.isOwn ? 'Vous' : contact.followedBy}</AboutRow>
      </div>
    </Section>
  </div>
);

const ExchangeIcon: React.FC<{ exchange: ContactExchange }> = ({ exchange }) => {
  if (exchange.kind === 'sms') return <MessageOutlined />;
  if (exchange.kind === 'voicemail') return <AudioOutlined />;
  if (exchange.kind === 'receptionist') return <ConciergeBellIcon />;
  return <PhoneOutlined />;
};

const ExchangesTab: React.FC<{ contact: ContactDisplay }> = ({ contact }) => {
  const { state, actions } = useContactExchangesController(contact.id);

  return (
    <div className="contacts__drawer-sections">
      <div className="contacts__exchanges-filters">
        {CONTACT_EXCHANGES_FILTERS.map((option) => (
          <button
            key={option.value}
            type="button"
            className={classNames('contacts__exchanges-filter', {
              'contacts__exchanges-filter--active': option.value === state.filter,
            })}
            onClick={() => actions.setFilter(option.value)}
          >
            {option.value === 'receptionist' && <ConciergeBellIcon />}
            {option.label}
          </button>
        ))}
      </div>

      {state.isLoading ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : state.days.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={state.hasFailed ? 'Échanges indisponibles' : 'Aucun échange'} />
      ) : (
        state.days.map((day) => (
          <div key={day.timestamp} className="contacts__timeline">
            <div className="contacts__timeline-day">{formatActivityDay(day.timestamp)}</div>
            {day.exchanges.map((exchange) => (
              <div key={exchange.key} className="contacts__timeline-item">
                <span className={classNames('contacts__timeline-icon', { 'contacts__timeline-icon--missed': exchange.isMissed })}>
                  <ExchangeIcon exchange={exchange} />
                </span>
                <div className="contacts__timeline-card">
                  <div className="contacts__timeline-head">
                    <span className="contacts__timeline-time">{dayjs(exchange.timestamp).format('HH:mm')}</span>
                    <strong className={classNames({ 'contacts__timeline-title--missed': exchange.isMissed })}>{exchange.title}</strong>
                  </div>
                  {/* Call summaries are markdown, SMS are plain text */}
                  {exchange.text &&
                    (exchange.kind === 'sms' ? (
                      <div className="contacts__timeline-message">{exchange.text}</div>
                    ) : (
                      <div className="contacts__timeline-message contacts__timeline-message--markdown">
                        <ReactMarkdown>{exchange.text}</ReactMarkdown>
                      </div>
                    ))}
                </div>
              </div>
            ))}
          </div>
        ))
      )}
    </div>
  );
};

const ContactDrawer: React.FC<ContactDrawerProps> = ({ contact, onClose, canOpenConversation = true }) => {
  const navigate = useNavigate();

  const openConversation = () => {
    if (!contact) return;
    const params = new URLSearchParams({ subscription: contact.subscriptionId, phone: contact.rawPhone });
    navigate(`${CONVERSATIONS_PAGE_PATH}?${params.toString()}`);
  };

  return (
    <Drawer
      open={!!contact}
      onClose={onClose}
      width={640}
      closable={false}
      className="contacts__drawer"
      title={
        contact && (
          <div className="contacts__drawer-header">
            <span className="contacts__avatar contacts__avatar--large">{contact.initials}</span>
            <div className="contacts__drawer-identity">
              <div className="contacts__drawer-name">{contact.fullName}</div>
              <div className="contacts__drawer-owner">{contact.isOwn ? 'Suivi par vous' : `Suivi par ${contact.followedBy}`}</div>
              {contact.isConfidential && (
                <span className="contacts__confidential-badge">
                  <LockOutlined />
                  Confidentiel - non enregistré
                </span>
              )}
            </div>
          </div>
        )
      }
      extra={
        <div className="contacts__drawer-actions">
          {contact && !contact.isOwn ? (
            <span className="contacts__read-only">Lecture seule</span>
          ) : (
            <Tooltip title={SOON_LABEL}>
              <Button icon={<EditOutlined />} disabled aria-label="Modifier" />
            </Tooltip>
          )}
          {canOpenConversation && (
            <Tooltip title="Conversation">
              <Button
                icon={<MessageOutlined />}
                disabled={!contact?.rawPhone}
                onClick={openConversation}
                aria-label="Conversation"
              />
            </Tooltip>
          )}
          <Button icon={<CloseOutlined />} onClick={onClose} aria-label="Fermer" />
        </div>
      }
    >
      {contact && (
        <>
          {/* A same number has one contact per line: the one of another line is read only */}
          {!contact.isOwn && (
            <div className="contacts__read-only-banner">
              <LockOutlined />
              Cette fiche appartient à {contact.followedBy}. Ses informations sont en lecture seule.
            </div>
          )}
          <Tabs
            key={contact.id}
            className="contacts__drawer-tabs"
            items={[
              { key: 'overview', label: "Vue d'ensemble", children: <OverviewTab contact={contact} /> },
              { key: 'informations', label: 'Informations', children: <InformationsTab contact={contact} /> },
              { key: 'exchanges', label: 'Échanges', children: <ExchangesTab contact={contact} /> },
            ]}
          />
        </>
      )}
    </Drawer>
  );
};

export default ContactDrawer;
